import React, { useCallback, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Users, User, AlertTriangle, Search, ShieldAlert } from "lucide-react";
import { useSuiteContext } from "./SuiteContextProvider";
import { UnsavedChangesDialog } from "./UnsavedChangesDialog";

/**
 * ImpersonationTool — Foundation Identity, funzione SIMULAZIONE separata.
 *
 * Componente distinto dal cambio contesto operativo (ContextSwitcher).
 * Disponibile SOLO agli utenti autorizzati (di norma super_admin/admin;
 * la visibilita' e' governata dal backend via `available.can_impersonate`).
 *
 * A differenza del cambio contesto, la simulazione:
 *   - richiede la scelta di una Persona da simulare;
 *   - richiede il MOTIVO obbligatorio;
 *   - registra un audit dedicato `context.impersonate.start`;
 *   - attiva il banner giallo persistente (via <ImpersonationBanner />);
 *   - si termina esplicitamente ("Termina simulazione").
 */
export function ImpersonationTool({ trigger }) {
  const {
    available, impersonables, startImpersonation,
  } = useSuiteContext();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [unsavedOpen, setUnsavedOpen] = useState(false);
  const [pending, setPending] = useState(null);

  const canImpersonate = !!available?.can_impersonate;

  const filtered = (impersonables || []).filter((u) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (u.display_name || "").toLowerCase().includes(q)
        || (u.tax_code || "").toLowerCase().includes(q)
        || (u.company_name || "").toLowerCase().includes(q);
  }).slice(0, 80);

  const doStart = useCallback(async (opts = {}) => {
    if (!selected || (reason || "").trim().length < 3) return;
    setSubmitting(true);
    try {
      const nextCtx = await startImpersonation(selected.user_id, reason.trim(), opts);
      toast.warning(`Simulazione avviata: ${selected.display_name}`, {
        description: (nextCtx?.breadcrumb || []).join(" · "),
      });
      // reset
      setOpen(false);
      setSelected(null);
      setReason("");
      setSearch("");
    } catch (e) {
      if (e?.code === "UNSAVED_CHANGES") {
        setPending({ selected, reason });
        setUnsavedOpen(true);
        return;
      }
      const detail = e?.response?.data?.detail || e?.message || "Errore";
      toast.error(`Simulazione negata: ${detail}`);
    } finally {
      setSubmitting(false);
    }
  }, [selected, reason, startImpersonation]);

  const onDiscardAndProceed = useCallback(async () => {
    setUnsavedOpen(false);
    if (!pending) return;
    await doStart({ discard: true });
    setPending(null);
  }, [pending, doStart]);

  if (!canImpersonate) {
    // Non renderizza nulla se l'utente non ha permesso di simulare.
    return null;
  }

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          {trigger || (
            <Button variant="outline" size="sm" data-testid="impersonation-tool-trigger">
              <ShieldAlert className="mr-2 h-4 w-4 text-amber-600" />
              Simula Persona
            </Button>
          )}
        </DialogTrigger>
        <DialogContent className="max-w-lg" data-testid="impersonation-tool-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-600" />
              Simulazione Persona (Impersonation)
            </DialogTitle>
            <DialogDescription>
              Funzione riservata: opererai temporaneamente come un'altra Persona
              della Suite. Ogni azione verra' registrata nell'audit di sistema.
            </DialogDescription>
          </DialogHeader>

          <Alert className="bg-amber-50 border-amber-200">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertTitle>Attenzione</AlertTitle>
            <AlertDescription>
              La simulazione non e' un cambio contesto: e' un accesso vicario
              tracciato. Non usare per operazioni ordinarie.
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Label>Cerca la Persona da simulare</Label>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Nome, Codice Fiscale, azienda…"
                className="pl-8"
                data-testid="impersonation-search"
              />
            </div>
            <div className="max-h-56 overflow-y-auto rounded-md border">
              {filtered.length === 0 && (
                <div className="py-6 text-center text-xs text-slate-400">
                  Nessuna Persona simulabile trovata
                </div>
              )}
              {filtered.map((u) => {
                const isSel = selected?.user_id === u.user_id;
                return (
                  <button
                    key={u.user_id}
                    type="button"
                    onClick={() => setSelected(u)}
                    className={
                      "flex w-full items-start gap-2 border-b p-2.5 text-left transition-colors " +
                      (isSel ? "bg-amber-50 border-amber-200" : "hover:bg-slate-50")
                    }
                    data-testid={`impersonation-candidate-${u.user_id}`}
                  >
                    <User className="mt-0.5 h-4 w-4 text-slate-400" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{u.display_name}</div>
                      <div className="truncate text-xs text-slate-500">
                        {u.current_role_display}
                        {u.company_name ? ` · ${u.company_name}` : ""}
                        {u.tax_code ? ` · ${u.tax_code}` : ""}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {selected && (
            <div className="space-y-2">
              <Label htmlFor="imp-reason">
                Motivo (obbligatorio, minimo 3 caratteri)
              </Label>
              <Textarea
                id="imp-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Es. verifica flusso cartellino"
                maxLength={200}
                data-testid="impersonation-reason"
              />
              <p className="text-xs text-slate-500">
                Verrà registrato nell'audit insieme all'ora e all'IP.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => { setOpen(false); setSelected(null); setReason(""); }}>
              Annulla
            </Button>
            <Button
              onClick={() => doStart()}
              disabled={!selected || reason.trim().length < 3 || submitting}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="impersonation-confirm-btn"
            >
              Avvia simulazione
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UnsavedChangesDialog
        open={unsavedOpen}
        onClose={() => { setUnsavedOpen(false); setPending(null); }}
        onDiscard={onDiscardAndProceed}
      />
    </>
  );
}
